
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { CheckCircle, XCircle, AlertCircle, RefreshCw } from "lucide-react";

export default function WebhookTesting() {
  const [testEmail, setTestEmail] = useState("support2@axxsis.com");
  const [loading, setLoading] = useState(false);
  const [upgradeLoading, setUpgradeLoading] = useState(false);
  const [userStatus, setUserStatus] = useState<any>(null);

  const checkUserStatus = async () => {
    if (!testEmail) return;
    
    setLoading(true);
    try {
      // Get user details
      const { data: profiles, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .ilike('username', testEmail.split('@')[0])
        .limit(1);

      if (profileError) throw profileError;

      if (!profiles || profiles.length === 0) {
        setUserStatus({ error: 'User not found' });
        return;
      }

      const userId = profiles[0].id;

      // Get subscription info
      const { data: subscription, error: subError } = await supabase
        .from('user_subscriptions')
        .select(`
          *,
          pricing_plans (
            name,
            type,
            price_cents
          )
        `)
        .eq('user_id', userId)
        .eq('status', 'active')
        .single();

      // Get credits info
      const { data: credits, error: creditsError } = await supabase
        .from('user_credits')
        .select('*')
        .eq('user_id', userId)
        .single();

      setUserStatus({
        userId,
        profile: profiles[0],
        subscription: subscription || null,
        credits: credits || null,
        subError: subError?.code === 'PGRST116' ? null : subError,
        creditsError: creditsError?.code === 'PGRST116' ? null : creditsError
      });

    } catch (error: any) {
      console.error('Error checking user status:', error);
      setUserStatus({ error: error.message });
      toast.error('Failed to check user status');
    } finally {
      setLoading(false);
    }
  };

  const manualUpgrade = async () => {
    if (!userStatus?.userId) return;
    
    setUpgradeLoading(true);
    try {
      // Find the monthly plan
      const { data: monthlyPlan, error: planError } = await supabase
        .from('pricing_plans')
        .select('*')
        .eq('type', 'monthly')
        .single();

      if (planError) throw planError;

      // Cancel existing subscriptions
      const { error: cancelError } = await supabase
        .from('user_subscriptions')
        .update({ status: 'canceled' })
        .eq('user_id', userStatus.userId)
        .eq('status', 'active');

      if (cancelError) throw cancelError;

      // Create new monthly subscription
      const { error: createError } = await supabase
        .from('user_subscriptions')
        .insert({
          user_id: userStatus.userId,
          plan_id: monthlyPlan.id,
          status: 'active',
          stripe_customer_id: 'manual-upgrade',
          stripe_subscription_id: 'manual-upgrade-' + Date.now()
        });

      if (createError) throw createError;

      // Update credits
      const { error: creditsError } = await supabase
        .from('user_credits')
        .upsert({
          user_id: userStatus.userId,
          credits_balance: monthlyPlan.credits_per_period,
          credits_used: 0,
          last_reset_at: new Date().toISOString()
        });

      if (creditsError) throw creditsError;

      toast.success('User manually upgraded to monthly plan');
      checkUserStatus(); // Refresh status
    } catch (error: any) {
      console.error('Error upgrading user:', error);
      toast.error('Failed to upgrade user: ' + error.message);
    } finally {
      setUpgradeLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5" />
            Webhook Testing & User Recovery
          </CardTitle>
          <CardDescription>
            Test webhook functionality and manually upgrade users affected by webhook failures
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2">
            <div className="flex-1">
              <Label htmlFor="testEmail">User Email/Username</Label>
              <Input
                id="testEmail"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="Enter user email or username"
              />
            </div>
            <Button 
              onClick={checkUserStatus} 
              disabled={loading}
              className="mt-6"
            >
              {loading ? <RefreshCw className="h-4 w-4 animate-spin" /> : "Check Status"}
            </Button>
          </div>

          {userStatus && (
            <div className="space-y-4">
              {userStatus.error ? (
                <Alert variant="destructive">
                  <XCircle className="h-4 w-4" />
                  <AlertDescription>{userStatus.error}</AlertDescription>
                </Alert>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <span className="font-medium">User Found: {userStatus.profile.username}</span>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Current Subscription</Label>
                      <div className="flex items-center gap-2 mt-1">
                        {userStatus.subscription ? (
                          <Badge variant={userStatus.subscription.pricing_plans.type === 'free' ? 'secondary' : 'default'}>
                            {userStatus.subscription.pricing_plans.name} - {userStatus.subscription.pricing_plans.type}
                          </Badge>
                        ) : (
                          <Badge variant="secondary">No Active Subscription</Badge>
                        )}
                      </div>
                    </div>

                    <div>
                      <Label>Credits Status</Label>
                      <div className="flex items-center gap-2 mt-1">
                        {userStatus.credits ? (
                          <Badge variant="outline">
                            {userStatus.credits.credits_balance} / {userStatus.credits.credits_balance + userStatus.credits.credits_used} credits
                          </Badge>
                        ) : (
                          <Badge variant="secondary">No Credits Record</Badge>
                        )}
                      </div>
                    </div>
                  </div>

                  {userStatus.subscription?.pricing_plans.type === 'free' && (
                    <Alert>
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>
                        This user appears to be on a free plan but may have made a payment. 
                        Use the manual upgrade button to fix their subscription.
                      </AlertDescription>
                    </Alert>
                  )}

                  <Button 
                    onClick={manualUpgrade}
                    disabled={upgradeLoading}
                    variant="outline"
                    className="w-full"
                  >
                    {upgradeLoading ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : null}
                    Manually Upgrade to Monthly Plan
                  </Button>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Next Steps</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 text-sm">
            <p>1. ✅ Updated webhook secret from test to live mode</p>
            <p>2. ✅ Enhanced webhook logging for better debugging</p>
            <p>3. 🔄 Test a small transaction to verify webhook processing</p>
            <p>4. 🔄 Monitor webhook logs for successful signature verification</p>
            <p>5. 🔄 Use manual upgrade tool for affected users if needed</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
